import { useEffect, useState, useMemo } from "react";
import { api, fmtEUR } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { planContext } from "@/lib/plan";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Abbr } from "@/components/Abbr";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Download, TrendingUp, TrendingDown, Wallet, FileSpreadsheet, FileText, BookOpen,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell, Line, LineChart,
} from "recharts";

const CATEGORY_COLORS = ["#0F766E", "#14B8A6", "#F59E0B", "#EF4444", "#8B5CF6", "#3B82F6"];
const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];
const MONTHS_SHORT = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Aoû","Sep","Oct","Nov","Déc"];

export default function FinancesPage() {
  const { user } = useAuth();
  const { isDemo } = planContext(user);
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [viewMode, setViewMode] = useState("annuel");
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear - 3; y <= currentYear + 1; y++) arr.push(String(y));
    return arr;
  }, [currentYear]);

  const load = async (y) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/finances/rentability?year=${y}`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(year); /* eslint-disable-next-line */ }, [year]);

  const download = async (path, filename) => {
    if (isDemo) return;
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  if (!data && loading) return <div className="text-sm text-slate-500">Chargement…</div>;
  if (!data) return null;

  const { items, monthly, breakdown, totals } = data;
  const breakdownFiltered = breakdown.filter((b) => b.amount > 0);
  const monthlyChart = monthly.map((m, i) => ({ ...m, month: MONTHS_SHORT[i] }));

  // Vue mensuelle : index + agrégats du mois sélectionné
  const monthIdx = Number(month) - 1;
  const isMonthly = viewMode === "mensuel";
  const mAgg = monthly[monthIdx] || { revenus: 0, depenses: 0, cashflow: 0 };
  const mTheorique = items.reduce((s, it) => s + (it.monthly?.[monthIdx]?.loyer_theorique || 0), 0);

  return (
    <div className="space-y-6" data-testid="finances-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Finances</h1>
          <p className="mt-1 text-sm text-slate-500">Rentabilité, cashflow et documents comptables.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={viewMode} onValueChange={setViewMode}>
            <SelectTrigger data-testid="finances-view-mode" className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="annuel">Vue annuelle</SelectItem>
              <SelectItem value="mensuel">Vue mensuelle</SelectItem>
            </SelectContent>
          </Select>
          {isMonthly && (
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger data-testid="finances-month" className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="finances-year" className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((y) => <SelectItem key={y} value={y}>Année {y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="export-csv" variant="outline" disabled={isDemo} onClick={() => download(`/finances/export.csv?year=${year}`, `finances_${year}.csv`)}>
            <FileSpreadsheet className="h-4 w-4 mr-2" /> CSV rentabilité
          </Button>
          <Button data-testid="export-2044" variant="outline" disabled={isDemo} title="Formulaire fiscal n°2044 — déclaration des revenus fonciers (location nue)" onClick={() => download(`/finances/export-2044.csv?year=${year}`, `declaration_2044_${year}.csv`)}>
            <FileText className="h-4 w-4 mr-2" /> Déclaration 2044
          </Button>
          <Button data-testid="export-compta" disabled={isDemo} onClick={() => download(`/finances/export-comptabilite.csv?year=${year}`, `comptabilite_${year}.csv`)} className="bg-[hsl(var(--primary))]">
            <BookOpen className="h-4 w-4 mr-2" /> Comptabilité
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {isMonthly ? (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4" data-testid="finances-kpis-monthly">
          <Card className="p-5 border-stone-200 shadow-none">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Revenus encaissés · {MONTHS[monthIdx]}</div>
            <div className="mt-2 font-heading text-2xl font-bold text-emerald-700">{fmtEUR(mAgg.revenus)}</div>
            <div className="text-xs text-slate-500 mt-1">sur {fmtEUR(mTheorique)} théoriques</div>
            {Number(mAgg.caf || 0) > 0 && (
              <div className="text-xs text-slate-500 mt-0.5" data-testid="caf-month-sub">dont CAF : <span className="font-medium text-slate-700">{fmtEUR(mAgg.caf)}</span></div>
            )}
          </Card>
          <Card className="p-5 border-stone-200 shadow-none">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dépenses · {MONTHS[monthIdx]}</div>
            <div className="mt-2 font-heading text-2xl font-bold text-rose-700">{fmtEUR(mAgg.depenses)}</div>
            <div className="text-xs text-slate-500 mt-1">charges lissées + travaux du mois</div>
          </Card>
          <Card className="p-5 border-stone-200 shadow-none">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cashflow · {MONTHS[monthIdx]}</div>
            <div className={`mt-2 font-heading text-2xl font-bold ${mAgg.cashflow >= 0 ? "text-[hsl(var(--primary))]" : "text-rose-700"}`}>
              {fmtEUR(mAgg.cashflow)}
            </div>
            <div className="text-xs text-slate-500 mt-1">encaissé − dépensé</div>
          </Card>
          <Card className="p-5 border-stone-200 shadow-none">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Taux d'encaissement · {MONTHS[monthIdx]}</div>
            <div className="mt-2 font-heading text-2xl font-bold">
              {mTheorique > 0 ? Math.round((mAgg.revenus / mTheorique) * 100) : 0}%
            </div>
            <div className="text-xs text-slate-500 mt-1">réel / théorique</div>
          </Card>
        </div>
      ) : (
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="p-5 border-stone-200 shadow-none">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Revenus encaissés</div>
          <div className="mt-2 font-heading text-2xl font-bold text-emerald-700">{fmtEUR(totals.revenus_reels)}</div>
          <div className="text-xs text-slate-500 mt-1">sur {fmtEUR(totals.revenus_annuels_theoriques)} théoriques</div>
          {Number(totals.caf_total || 0) > 0 && (
            <div className="text-xs text-slate-500 mt-0.5" data-testid="caf-total-sub">dont CAF : <span className="font-medium text-slate-700">{fmtEUR(totals.caf_total)}</span></div>
          )}
        </Card>
        <Card className="p-5 border-stone-200 shadow-none">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Dépenses totales</div>
          <div className="mt-2 font-heading text-2xl font-bold text-rose-700">{fmtEUR(totals.depenses_totales)}</div>
          <div className="text-xs text-slate-500 mt-1">charges + taxes + travaux</div>
        </Card>
        <Card className="p-5 border-stone-200 shadow-none">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cashflow réel</div>
          <div className={`mt-2 font-heading text-2xl font-bold ${totals.cashflow_reel >= 0 ? "text-[hsl(var(--primary))]" : "text-rose-700"}`}>
            {fmtEUR(totals.cashflow_reel)}
          </div>
          <div className="text-xs text-slate-500 mt-1">encaissé − dépensé</div>
        </Card>
        <Card className="p-5 border-stone-200 shadow-none">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Taux d'encaissement</div>
          <div className="mt-2 font-heading text-2xl font-bold">
            {totals.revenus_annuels_theoriques > 0
              ? Math.round((totals.revenus_reels / totals.revenus_annuels_theoriques) * 100)
              : 0}%
          </div>
          <div className="text-xs text-slate-500 mt-1">réel / théorique</div>
        </Card>
      </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 p-5 border-stone-200 shadow-none" data-testid="cashflow-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Cashflow mensuel {year}</div>
              <div className="font-heading text-lg font-semibold mt-1">Recettes vs dépenses</div>
            </div>
            <div className="hidden sm:flex items-center gap-1 text-xs text-[hsl(var(--primary))] bg-[hsl(var(--accent))] rounded-md px-2 py-1">
              <TrendingUp className="h-3.5 w-3.5" /> Vue mensuelle
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyChart} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" vertical={false} />
                <XAxis dataKey="month" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip formatter={(v) => fmtEUR(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="revenus" name="Recettes" fill="#0F766E" radius={[6, 6, 0, 0]} />
                <Bar dataKey="depenses" name="Dépenses" fill="#F59E0B" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-stone-200 shadow-none" data-testid="breakdown-chart">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Répartition des dépenses</div>
          <div className="font-heading text-lg font-semibold mt-1 mb-3">{isMonthly ? `Année ${year} (annuel)` : year}</div>
          {breakdownFiltered.length === 0 ? (
            <div className="text-sm text-slate-500 py-10 text-center">Aucune dépense enregistrée.</div>
          ) : (
            <>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={breakdownFiltered}
                      dataKey="amount"
                      nameKey="category"
                      innerRadius={45}
                      outerRadius={80}
                      paddingAngle={3}
                    >
                      {breakdownFiltered.map((_, i) => (
                        <Cell key={i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => fmtEUR(v)} contentStyle={{ borderRadius: 8, border: "1px solid #E7E5E4", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-2">
                {breakdownFiltered.map((b, i) => (
                  <div key={b.category} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full" style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                      <span className="text-slate-600">{b.category}</span>
                    </div>
                    <span className="font-medium">{fmtEUR(b.amount)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Details par bien */}
      <Card className="border-stone-200 shadow-none">
        <div className="p-4 border-b border-stone-100">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Détail par bien</div>
          <div className="text-sm text-slate-700 mt-0.5">
            {isMonthly ? `Rentabilité — ${MONTHS[monthIdx]} ${year}` : `Rentabilité annuelle ${year}`}
          </div>
        </div>
        {items.length === 0 ? (
          <div className="p-12 text-center text-sm text-slate-500">Aucun bien à analyser.</div>
        ) : (
          <>
          {/* Vue mobile : une carte par bien (le tableau déborde sur petit écran) */}
          <div className="md:hidden divide-y divide-stone-100" data-testid="finance-cards-mobile">
            {items.map((it) => {
              if (isMonthly) {
                const m = it.monthly?.[monthIdx] || { revenus: 0, depenses: 0, cashflow: 0, loyer_theorique: 0 };
                const taux = m.loyer_theorique > 0 ? Math.round((m.revenus / m.loyer_theorique) * 100) : 0;
                return (
                  <div key={it.property_id} data-testid={`finance-card-m-${it.property_id}`} className="p-4 space-y-2.5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{it.label}</div>
                        <div className="text-xs text-slate-500">{it.city}</div>
                      </div>
                      <Badge className={`border-0 shrink-0 ${taux >= 100 ? "bg-emerald-100 text-emerald-800" : taux > 0 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>{taux}%</Badge>
                    </div>
                    <dl className="space-y-1.5 text-sm">
                      <div className="flex justify-between"><dt className="text-slate-500">Loyer du mois</dt><dd>{fmtEUR(m.loyer_theorique)}</dd></div>
                      <div className="flex justify-between"><dt className="text-slate-500">Encaissé</dt><dd className="text-emerald-700">{fmtEUR(m.revenus)}</dd></div>
                      {Number(m.caf || 0) > 0 && <div className="flex justify-between"><dt className="text-slate-500">dont CAF</dt><dd className="text-slate-500">{fmtEUR(m.caf)}</dd></div>}
                      <div className="flex justify-between"><dt className="text-slate-500">Dépenses</dt><dd className="text-slate-600">{fmtEUR(m.depenses)}</dd></div>
                      <div className="flex justify-between"><dt className="text-slate-500">Cashflow</dt><dd className={`font-medium ${m.cashflow >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtEUR(m.cashflow)}</dd></div>
                    </dl>
                  </div>
                );
              }
              return (
                <div key={it.property_id} data-testid={`finance-card-${it.property_id}`} className="p-4 space-y-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{it.label}</div>
                      <div className="text-xs text-slate-500">{it.city}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {it.rendement_brut !== null && <Badge className="bg-[hsl(var(--accent))] text-[hsl(var(--primary))] border-0">{it.rendement_brut}%</Badge>}
                      {it.rendement_net !== null && <Badge className={`border-0 ${it.rendement_net >= 4 ? "bg-emerald-100 text-emerald-800" : it.rendement_net >= 2 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>net {it.rendement_net}%</Badge>}
                    </div>
                  </div>
                  <dl className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><dt className="text-slate-500">Loyer annuel</dt><dd>{fmtEUR(it.rent_annual)}</dd></div>
                    <div className="flex justify-between"><dt className="text-slate-500">Encaissé</dt><dd className="text-emerald-700">{fmtEUR(it.revenus_reels)}</dd></div>
                    {Number(it.caf_annuel || 0) > 0 && <div className="flex justify-between"><dt className="text-slate-500">dont CAF</dt><dd className="text-slate-500">{fmtEUR(it.caf_annuel)}</dd></div>}
                    <div className="flex justify-between"><dt className="text-slate-500">Dépenses</dt><dd className="text-slate-600">{fmtEUR(it.depenses_totales)}</dd></div>
                    <div className="flex justify-between pl-3 text-xs text-slate-400"><dt><Abbr short="Ch" full="Charges annuelles (hors taxe foncière)" /></dt><dd>{fmtEUR(it.charges_annuelles)}</dd></div>
                    <div className="flex justify-between pl-3 text-xs text-slate-400"><dt><Abbr short="TF" full="Taxe Foncière annuelle" /></dt><dd>{fmtEUR(it.taxe_fonciere)}</dd></div>
                    <div className="flex justify-between"><dt className="text-slate-500">Cashflow</dt><dd className={`font-medium ${it.cashflow_annuel >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtEUR(it.cashflow_annuel)}</dd></div>
                  </dl>
                </div>
              );
            })}
          </div>
          {/* Vue desktop : tableau complet */}
          <div className="hidden md:block overflow-x-auto">
          {isMonthly ? (
          <Table data-testid="finance-table-monthly">
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Bien</TableHead>
                <TableHead>Loyer du mois</TableHead>
                <TableHead>Encaissé</TableHead>
                <TableHead>dont CAF</TableHead>
                <TableHead>Dépenses</TableHead>
                <TableHead>Cashflow</TableHead>
                <TableHead>Taux</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => {
                const m = it.monthly?.[monthIdx] || { revenus: 0, depenses: 0, cashflow: 0, loyer_theorique: 0 };
                const taux = m.loyer_theorique > 0 ? Math.round((m.revenus / m.loyer_theorique) * 100) : 0;
                return (
                  <TableRow key={it.property_id} data-testid={`finance-row-m-${it.property_id}`}>
                    <TableCell>
                      <div className="font-medium">{it.label}</div>
                      <div className="text-xs text-slate-500">{it.city}</div>
                    </TableCell>
                    <TableCell className="text-sm">{fmtEUR(m.loyer_theorique)}</TableCell>
                    <TableCell className="text-sm text-emerald-700">{fmtEUR(m.revenus)}</TableCell>
                    <TableCell className="text-sm text-slate-500">{Number(m.caf || 0) > 0 ? fmtEUR(m.caf) : "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{fmtEUR(m.depenses)}</TableCell>
                    <TableCell className={`text-sm font-medium ${m.cashflow >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {fmtEUR(m.cashflow)}
                    </TableCell>
                    <TableCell>
                      <Badge className={`border-0 ${taux >= 100 ? "bg-emerald-100 text-emerald-800" : taux > 0 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>{taux}%</Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Bien</TableHead>
                <TableHead>Loyer annuel</TableHead>
                <TableHead>Encaissé</TableHead>
                <TableHead>dont CAF</TableHead>
                <TableHead>Dépenses</TableHead>
                <TableHead>Cashflow</TableHead>
                <TableHead>Rdt brut</TableHead>
                <TableHead>Rdt net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.property_id} data-testid={`finance-row-${it.property_id}`}>
                  <TableCell>
                    <div className="font-medium">{it.label}</div>
                    <div className="text-xs text-slate-500">{it.city}</div>
                  </TableCell>
                  <TableCell className="text-sm">{fmtEUR(it.rent_annual)}</TableCell>
                  <TableCell className="text-sm text-emerald-700">{fmtEUR(it.revenus_reels)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{Number(it.caf_annuel || 0) > 0 ? fmtEUR(it.caf_annuel) : "—"}</TableCell>
                  <TableCell className="text-sm text-slate-600">
                    <div>{fmtEUR(it.depenses_totales)}</div>
                    <div className="text-xs text-slate-400">
                      <Abbr short="Ch" full="Charges annuelles (hors taxe foncière)" /> {fmtEUR(it.charges_annuelles)} · <Abbr short="TF" full="Taxe Foncière annuelle" /> {fmtEUR(it.taxe_fonciere)}
                    </div>
                  </TableCell>
                  <TableCell className={`text-sm font-medium ${it.cashflow_annuel >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {fmtEUR(it.cashflow_annuel)}
                  </TableCell>
                  <TableCell>
                    {it.rendement_brut !== null
                      ? <Badge className="bg-[hsl(var(--accent))] text-[hsl(var(--primary))] border-0">{it.rendement_brut}%</Badge>
                      : <span className="text-xs text-slate-400">Prix ?</span>}
                  </TableCell>
                  <TableCell>
                    {it.rendement_net !== null
                      ? <Badge className={`border-0 ${it.rendement_net >= 4 ? "bg-emerald-100 text-emerald-800" : it.rendement_net >= 2 ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-800"}`}>{it.rendement_net}%</Badge>
                      : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )}
          </div>
          </>
        )}
      </Card>

      <div className="text-xs text-slate-400 leading-relaxed">
        <b>Astuce fiscale</b> : le CSV "Déclaration 2044" pré-remplit les lignes standard du régime réel (loyers encaissés, charges déductibles, taxe foncière, travaux). Les intérêts d'emprunt et l'assurance PNO restent à ajouter manuellement selon votre situation.
      </div>
    </div>
  );
}
